import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { ResidentStatus } from "./Residents";

/**
 * 家族 API 操作クラス
 */
export default class Families {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Families
   */
  public async getsInFacility(
    userStore: LoginUser,
    queryParams?: {
      startIndex?: number;
      endIndex?: number;
      sortKey?: string;
      order?: string;
      senderType?: SenderType;
      masking?: string;
      addDeleted?: "true" | "false";
    }
  ): Promise<Array<FamilyGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.FamiliesInFacility, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<FamilyGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET Families
   */
  public async getsInResident(
    userStore: LoginUser,
    residentId: number,
    queryParams?: {
      startIndex?: number;
      endIndex?: number;
      sortKey: string;
      order: string;
      senderType?: SenderType;
      masking?: string;
    }
  ): Promise<Array<FamilyGetModel>> {
    // 施設ID、利用者IDの場合のURL作成
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Families, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<FamilyGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET FamiliesCount
   */
  public async getCountInFacility(
    userStore: LoginUser,
    queryParams?: {
      senderType?: SenderType;
    }
  ): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.FamiliesInFacility, formatParams);
    const params: any = queryParams ? queryParams : {};
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * GET FamiliesCount
   */
  public async getCountInResident(
    userStore: LoginUser,
    residentId: number,
    queryParams?: {
      senderType?: SenderType;
    }
  ): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Families, formatParams);
    const params: any = queryParams ? queryParams : {};
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * PATCH Families
   */
  public async patch(userStore: LoginUser, residentId: number, familyId: number, requestBody: FamilyPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId,
      familyId: familyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Family, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Families
   */
  public async delete(userStore: LoginUser, residentId: number, familyId: number, requestBody: FamilyDeleteModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId,
      familyId: familyId,
      // 呼出し元種別は固定で0(施設アプリ)を設定する
      appType: 0
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DeleteFamily, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0, requestBody);

    return response.data;
  }
}

export interface FamilyGetModel {
  /** 利用者または家族の誕生日(YYYYMMDDhhmmssSSS) */
  birthday: any;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 施設を示す ID */
  facilityId: number;
  /** 性別 */
  gender: string;
  /** 登録日時(YYYYMMDDhhmmssSSS) */
  created: number;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 利用者ID */
  id: number;
  /** 利用者名 */
  name: string;
  /** 利用者（ふりがな） */
  kanaName: string;
  /** 電話番号 */
  tel: string;
  /** 部屋名 */
  roomId: number;
  /** 部屋名 */
  room: string;
  /** 利用者登録用パスワード */
  password: string;
  /** ソートキー (residentId)_(familyId) */
  residentsSortKey: string;
  /** グループID */
  groupId: number;
  /** 利用者グループ名 */
  groupName: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族の氏名 */
  familyName: string;
  /** 家族の電話番号 */
  familyTel: string;
  /** LINEのID */
  familyLineId?: string;
  /** 家族の生年月日 正しくはfamilyBirthdayだが、許容する */
  familyBirthDay: number;
  /** 家族の性別 */
  familyGender: string;
  /** 続柄 */
  familyRelation: string;
  /** 家族の役割 */
  familyPosition: string;
  /** 家族のメッセージ最終投稿日時(YYYYMMDDhhmmssSSS) */
  familyMessageLatestDate: number;
  /** 家族の最終投稿メッセージ内容（テキスト） */
  familyLatestMessage: string;
  /** 送信種別 */
  senderType: SenderType;
  /** 家族のメールアドレス */
  familyMailaddress?: string;
  /** 家族の機能使用可否 */
  familyPermittedFunctions: FamilyPermittedFunctions;

  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;

  /** 利用者状態 */
  status: ResidentStatus;
}

export enum SenderType {
  /** LINE */
  LINE = 0,
  /** MAIL */
  MAIL = 1
}

export interface FamilyPermittedFunctions {
  /** 家族からの連絡 */
  opinion: boolean;
  /** 写真印刷依頼 */
  picture: boolean;
}

export interface FamilyPatchModel {
  familyName: string;
  familyTel: string;
  familyRelation: string;
  familyGender: string;
  familyPosition?: string;
  familyBirthDay?: number;
  familyPermittedFunctions: FamilyPermittedFunctions;
  updated: number;
}

export interface FamilyDeleteModel {
  /** 削除メッセージタイトル */
  deleteMessageTitle: string | null;
  /** 削除メッセージ本文 */
  deleteMessageText: string | null;
  /** 待機者家族削除時に使用する、同時削除対象の施設ID */
  concurrentDeleteFacilityIds?: number[];
}
